<template>
  <div
    :class="[
      'sam-table d-flex flex-column between-lg',
      loading ? 'is-loading-table' : '',
      truncateRowHeaders ? 'is-truncated' : '',
      !rows.length ? 'is-empty' : '',
    ]"
    data-samora
  >
    <div
      v-if="$slots.title || title"
      class="sam-table__title-wrapper"
    >
      <slot
        v-if="$slots.title"
        name="title"
      />
      <h6
        v-else
        class="sam-table__title"
      >
        {{ title }}
      </h6>
    </div>

    <div class="sam-table__wrapper">
      <table class="sam-table__table">
        <thead class="sam-table__head">
          <tr>
            <template v-if="isSelectAll">
              <th
                width="40"
                class="sam-table__checkbox"
              >
                <SamCheckbox
                  class="table__select-all-checkbox"
                  :disabled="rows.length === 0 || disableSelection || disableAllSelection"
                  :data="allRowsSelected"
                  @change="onSelectAll"
                />
              </th>
            </template>
            <th
              v-for="(column, index) in modifiedColumns"
              :key="index"
              :width="column.width"
              :class="column.classes"
            >
              <div
                v-if="checkHeaderSlot(column)"
                class="sam-table__headerContent"
              >
                <slot
                  :name="`th-${column.id}`"
                  :column="column"
                  :index="index"
                />
                <button
                  v-if="column.sortable && !hideSortingOptions"
                  type="button"
                  class="sam-table__sortAction"
                  :class="sortIconClass(column)"
                  @click.prevent="changeSort(column)"
                  @keypress.prevent="changeSort(column)"
                >
                  <SamIcon
                    class="sam-table__sortIcon"
                    :icon="sortIcon(column)"
                  />
                </button>
              </div>

              <div
                v-else
                class="sam-table__headerContent"
              >
                <span
                  v-if="sortIconPosition(column) === ICON_POSITION.RIGHT"
                  :id="'table-label-' + index"
                  class="sam-table__headerContent--label"
                >
                  {{ column.label }}
                </span>
                <SamTooltip
                  v-if="column.tooltip"
                  :message="column.tooltip"
                  :position="'top'"
                  :anchor-name="'table-label-' + index"
                />
                <button
                  v-if="column.sortable && !hideSortingOptions"
                  type="button"
                  class="sam-table__sortAction"
                  :class="sortIconClass(column)"
                  @click.prevent="changeSort(column)"
                  @keypress.prevent="changeSort(column)"
                >
                  <SamIcon
                    class="sam-table__sortIcon"
                    :icon="sortIcon(column)"
                  />
                </button>
                <span
                  v-if="sortIconPosition(column) === ICON_POSITION.LEFT"
                  :id="'table-label-' + index"
                  class="sam-table__headerContent--label"
                >
                  {{ column.label }}
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody
          v-if="!loading"
          class="sam-table__body text--body-2"
        >
          <template v-if="$slots['table-body']">
            <slot
              name="table-body"
              :rows="rows"
            />
          </template>
          <template v-else-if="rows.length > 0">
            <tr
              v-for="(row, index) in rows"
              :key="row.id || index"
              :class="[{ clickable: row.clickable || clickable }, row.rowClass]"
              @click="onClickedRow(row, $event)"
              @keydown.enter="onClickedRow(row)"
            >
              <template v-if="isSelectAll">
                <td class="sam-table__checkbox">
                  <SamCheckbox
                    :data="row.selected"
                    :disabled="row.disabled || disableSelection"
                    class="table__select-row-checkbox"
                    @change="(selected) => onSelectRow(selected, row)"
                  />
                </td>
              </template>
              <!--
@slot Table body. Should use the `<td>` element
@binding {array} row The object that is being iterated
-->
              <slot
                name="items"
                :row="row"
              />
            </tr>
          </template>
          <template v-else>
            <tr>
              <td :colspan="modifiedColumns.length + (isSelectAll ? 1 : 0)">
                <!-- @slot Message to display if there's no elements -->
                <slot name="no-data" />
              </td>
            </tr>
          </template>
        </tbody>
        <tbody
          v-else
          class="sam-table__body"
        >
          <tr
            v-for="(row, rowIndex) in paginationItems"
            :key="rowIndex"
          >
            <td
              v-for="(col, colIndex) in modifiedColumns"
              :key="colIndex"
              class="is-loading-cell"
            >
              <span
                v-if="!col.preventLoading"
                class="sam-table__loadingCell"
              />
            </td>

            <td
              v-if="isSelectAll"
              class="is-loading-cell"
            >
              <!-- if selection possible we need to add an extra loading column -->
              <span class="sam-table__loadingCell" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="hasPagination"
      class="sam-table__paginationContainer"
    >
      <div
        class="text--body-2 text--light"
        :class="{ 'pl-6 pr-4': !!$slots['pagination-prepend'] }"
      >
        <slot name="pagination-prepend" />
      </div>
      <SamPagination
        class="py-0"
        :auto-hide="autoHidePagination"
        :current-page="currentPage"
        :total-pages="totalPages"
        :loading="loading"
        v-on="$listeners"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable vue-a11y/click-events-have-key-events */
import SamPagination from '../SamPagination/SamPagination.vue';
import SamCheckbox from '../SamCheckbox/SamCheckbox.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import SamTooltip from '../SamTooltip/SamTooltip.vue';

export const ICON_POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
};

export default {
  name: 'SamTable',
  components: {
    SamTooltip,
    SamIcon,
    SamCheckbox,
    SamPagination,
  },
  props: {
    /**
     * An array of objects representing column headers
     */
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * An array of objects representing row data
     */
    rows: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * Determines whether table pagination is shown
     */
    hasPagination: {
      type: Boolean,
      default: false,
    },
    /**
     * The value (index) of the current page
     */
    currentPage: {
      type: Number,
      default: 0,
    },
    /**
     * The total number of available pages
     */
    totalPages: {
      type: Number,
      default: 0,
    },
    /**
     * The number of items
     */
    paginationItems: {
      type: Number,
      default: 10,
    },
    /**
     * Displays a skeleton loading animation within the cells
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Displays a checkbox in the heading and another for each row
     */
    isSelectAll: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether select all is set to selected
     */
    selectAllState: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    /**
     * Hides overflown text and adds an ellipsis
     */
    truncateRowHeaders: {
      type: Boolean,
      default: false,
    },
    /**
     * An array of objects representing which columns are sortable
     * and their current sort order.
     */
    sortOptions: {
      type: Array,
      default: () => [],
    },
    /**
     * Whether multiple columns can be sorted or not
     */
    isMultiSort: {
      type: Boolean,
      default: false,
    },
    autoHidePagination: {
      type: Boolean,
      default: true,
    },
    /**
     * The table's title
     */
    title: {
      type: String,
      default: null,
    },
    /**
     * Whether to show a clickable cursor on all table rows
     */
    clickable: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to disable select boxes
     */
    disableSelection: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether to disable bulk select boxes
     */
    disableAllSelection: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { ICON_POSITION };
  },
  computed: {
    /**
     * Prepares the columns with sortable attribute
     * if sortOptions has anything.
     */
    modifiedColumns() {
      const tmpCols = this.columns;

      this.sortOptions.forEach((option) => {
        const column = Object.keys(option)[0];
        const sortValue = Object.values(option)[0];
        const index = tmpCols.findIndex((item) => item.id === column);

        tmpCols[index].sortable = true;
        tmpCols[index].current_sort = sortValue;
      });

      return tmpCols;
    },
    /**
     * Whether the sorting options should appear
     *
     * Right now only checking if the table as more that one child row
     */
    hideSortingOptions() {
      return this.totalPages <= 1 && this.rows.length <= 1;
    },
    allRowsSelected() {
      // if selectAllState is defined we look at that
      if (this.selectAllState !== undefined) {
        return this.selectAllState;
      }

      // otherwise we look at the current rows
      return this.rows.findIndex((r) => !r.selected) === -1;
    },
  },
  methods: {
    onSelectAll(selected) {
      /**
       * Triggers when clicks on heading checkbox
       * @event selectAll
       * @property {boolean} selected true if is checked, false if is unchecked
       */
      this.$emit('selectAll', selected);
    },
    onSelectRow(selected, row) {
      /**
       * Triggers when clicks on row checkbox
       * @event selectRow
       * @property {boolean} selected true if is checked, false if is unchecked
       * @property {object} row the row object that has emitted the event
       */
      this.$emit('selectRow', selected, row);
    },
    onClickedRow(row, $event) {
      $event.stopPropagation();
      /**
       * Triggers when clicks anywhere on row
       * @event clickedRow
       * @property {object} row the row object that has emitted the event
       */
      this.$emit('clickedRow', row);
    },
    /**
     * Returns the correct icon
     */
    sortIcon(column) {
      if (column.current_sort) {
        return column.current_sort === 'asc' ? 'arrow-chevron-up' : 'arrow-chevron-down';
      }

      return 'expand-less';
    },
    sortIconClass(column) {
      return column.current_sort ? 'active' : '';
    },
    sortIconPosition(column) {
      if ([ICON_POSITION.LEFT, ICON_POSITION.RIGHT].includes(column.sort_position)) {
        return column.sort_position;
      }

      return ICON_POSITION.RIGHT;
    },
    /**
     * Triggers change sort event with selected column ID and desired sort order
     * @event changeSort
     */
    changeSort(column) {
      const updatedSortOptions = this.sortOptions;
      const index = updatedSortOptions.findIndex((item) => Object.keys(item)[0] === column.id);
      const currentSort = Object.values(updatedSortOptions[index])[0];
      let newSort = '';

      // Cycle through possible sort options: asc, desc or null
      if (currentSort === 'asc') {
        newSort = 'desc';
      } else if (currentSort === 'desc') {
        newSort = null;
      } else {
        newSort = 'asc';
      }

      // If multisort is disabled, reset all columns sort options to null.
      if (!this.isMultiSort) {
        updatedSortOptions.forEach((option, i) => {
          updatedSortOptions[i] = { [Object.keys(option)[0]]: null };
        });
      }

      // Update the column interacted with with the newSort value
      updatedSortOptions[index] = { [column.id]: newSort };

      this.$emit('changeSort', updatedSortOptions);
    },
    /**
     * Checks if slot exists
     */
    checkHeaderSlot(column) {
      // eslint-disable-next-line eqeqeq
      if (column?.id == null) return false;
      return !!this.$slots[`th-${column.id}`];
    },
  },
};
</script>

<style lang="scss" scoped>
$table-border-color: $neutral--30;
$table-border: solid $size--0--fourth $table-border-color;
$sort-icon-wrapper-size: 27px;

.sam-table {
  $parent: &;
  border: $table-border;
  border-radius: $size--1;
  background-color: $primary--white;

  &__title-wrapper {
    @extend .py-4, .px-6;
    border-bottom: $table-border;
  }

  &.is-truncated {
    #{$parent}__body {
      th {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 200px;
      }
    }
  }

  &.is-loading-table .sam-table__body tr:hover,
  &.is-empty .sam-table__body tr:hover {
    background-color: transparent;
  }

  &__wrapper,
  &__body {
    width: 100%;
  }

  &__table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }

  &__head {
    border-bottom: $table-border;

    tr {
      th {
        @extend .px-4, .text--subtitle;
        text-align: left;
        vertical-align: middle;
        color: $neutral--90;
        height: 54px;

        &:first-child {
          padding-left: $size--6;
        }

        &:last-child {
          padding-right: $size--6;
        }
      }
    }
  }

  &__headerContent {
    @extend .d-flex, .items-center;
    column-gap: $size--2;

    &--label {
      @extend .text--ellipse;
    }
  }

  &__sortAction {
    @extend .p-0;
    display: table-cell;
    width: $sort-icon-wrapper-size;
    height: $sort-icon-wrapper-size;
    border: none;
    border-radius: $size--1;
    box-shadow: none;
    background-color: transparent;
    outline: none;
    transition: background-color 0.2s;
    cursor: pointer;

    &.active {
      background-color: $informative--surface;
      color: $informative--main;
    }

    &:hover {
      background-color: $informative--surface;
    }

    &:active {
      background-color: $informative--surface;
    }

    .sam-table__sortIcon {
      text-align: center;
      vertical-align: middle;
    }
  }

  &__body::v-deep {
    td,
    th {
      @extend .px-4, .text--body-2;
      text-align: start;
      vertical-align: middle;
      color: $neutral--90;
      height: 54px;

      &:first-child {
        padding-left: $size--6;
      }

      &:last-child {
        padding-right: $size--6;
      }

      &.is-loading-cell {
        span {
          display: block;
          height: $size--4;
          @include old-skeleton-blink;
        }
      }
    }

    tr {
      &.clickable {
        cursor: pointer;
      }

      transition: background-color 0.2s;

      &:not(:last-child) {
        border-bottom: $table-border;
      }

      a {
        color: $neutral--90;
        font-weight: 500;
      }
    }
  }

  &:not(.is-empty) tbody tr:hover {
    background-color: $informative--surface;

    td,
    th,
    a {
      color: $accent--unbabel-blue;
    }
  }

  &__paginationContainer {
    @extend .d-flex, .items-center, .between-xs;
    border-top: $table-border;
    height: 54px;
  }

  &__checkbox {
    @extend .py-0, .px-4;
    width: 61px; // padding + checkbox width

    .sam-checkbox {
      height: 25px;
    }
  }
}
</style>
